import { CheckCircleTwoTone, FileOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Form, Input, message, Modal, Select, Typography, Upload } from "antd";
import { useEffect, useState } from "react";
import DocumentService from "../services/Document/DocumentService";
import AppButton from "../common/AppButton";
import { faArrowUpFromBracket, faFile } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const { TextArea } = Input;

export default function DocumentModal({ isOpen, entityID, onClose, onSubmit, initialValues = null }) {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFileSelected, setIsFileSelected] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    open: false,
    title: '',
    body: null,
    okText: 'Yes',
    cancelText: 'No',
    type: 'warning',
    onOk: () => { },
  });

  const showConfirmation = (action, fileName, docTypeCount, onConfirm) => {
    const isMulti = docTypeCount > 1;
    const isDelete = action === 'delete';
    const title = isDelete ? 'Delete Confirmation' : 'Update Confirmation';


    let body;
    if (isDelete) {
      body = isMulti ? (
        <>
          <div>
            You are about to delete document{' '}
            <Typography.Text strong>{fileName}</Typography.Text>.
          </div>
          <br />
          <Typography.Text strong>
            This document is linked to {docTypeCount} Document Type entries. The
            document and related Document Type entries will all be deleted.
          </Typography.Text>
          <br /><br />
          <div>Please confirm that you wish to proceed.</div>
        </>
      ) : (
        <>
          <div>
            You are about to delete document{' '}
            <Typography.Text strong>{fileName}</Typography.Text>.
          </div>
          <div>Please confirm that you wish to proceed.</div>
        </>
      );
    } else {
      body =
        <>
          <div>
            You are about to replace document{' '}
            <Typography.Text strong>{fileName}</Typography.Text>.
          </div>
          <br />
          <Typography.Text strong>
            This document is linked to {docTypeCount} Document Type entries. The
            document and related Document Type entries will all be updated and
            linked to the new document being uploaded.
          </Typography.Text>
          <br /><br />
          <div>Please confirm that you wish to proceed.</div>
        </>;
    }
    setModalConfig({
      open: true,
      title,
      body,
      onOk: onConfirm,
      okText: 'Yes',
      cancelText: 'No',
      type: 'warning',
    });
  };
  useEffect(() => {
    fetchDocumentTypes();
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    if (!initialValues) {
      form.resetFields();
      setFileList([]);
      setIsFileSelected(false);
      return;
    }

    if (documentTypes.length > 0) {
      form.setFieldsValue({
        documentTypeID: initialValues.documentType?.id,
        fileName: initialValues.originalFileName,
        notes: initialValues.notes,
        entityType: initialValues.entityType || "Container",
      });
      if (initialValues.fileName) {
        setIsFileSelected(true);
        setFileList([
          {
            uid: "-1",
            name: initialValues.originalFileName,
            status: "done",
            url: initialValues.fileUrl || "",
          },
        ]);
      }
    }
  }, [isOpen, initialValues, documentTypes]);

  const fetchDocumentTypes = () => {
    setIsLoading(true);
    DocumentService.getAllDocumentTypes()
      .then((response) => {
        if (response.status === 200) {
          setDocumentTypes(response.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching document types:", error);
      })
      .finally(() => setIsLoading(false));
  };

  const handleFinish = async (values) => {
    const formData = {
      ...values,
      files: fileList.length > 0 ? fileList[0].originFileObj : null,
      entityID: entityID,
      id: initialValues && initialValues.id ? initialValues.id : null,
    };

    setIsSubmitting(true);

    try {
      await onSubmit(formData);
      await DocumentService.uploadDocument(formData);
      setSubmitSuccess(true);
      message.success("Document Saved Successfully");
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (error) {
      console.error("Error submitting form:", error);
      message.error("Error submitting form");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setFileList([]);
    setSubmitSuccess(false);
    setIsSubmitting(false);
    setIsFileSelected(false);
    onClose();
  };

  const handleDelete = () => {
    DocumentService.deleteDocument(initialValues.id)
      .then(() => {
        message.success("Document Deleted Successfully");
        handleClose();
        setTimeout(() => {
          window.location.reload();
        }, 500);
      })
      .catch((error) => {
        message.error(error?.response?.data?.message || "Failed to delete document");
      })
      .finally(() => { });
  };

  const uploadProps = {
    beforeUpload: (file) => {
      const blockedTypes = [
        "application/x-msdownload",
        "application/x-msdos-program",
        "application/x-sh",
        "application/x-bat",
        "application/x-cmd",
        "application/javascript",
        "text/javascript",
        "application/x-python-code",
        "text/x-python",
        "application/x-php",
        "application/x-ruby",
      ];
      const blockedExtensions = [
        ".exe",
        ".bat",
        ".cmd",
        ".sh",
        ".js",
        ".py",
        ".php",
        ".rb",
        ".msi",
        ".apk",
        ".vbs",
        ".wsf",
      ];
      const fileTypeBlocked = blockedTypes.includes(file.type);
      const fileNameBlocked = blockedExtensions.some((ext) =>
        file.name.toLowerCase().endsWith(ext)
      );
      if (fileTypeBlocked || fileNameBlocked) {
        message.error("Unsupported file type");
        return Upload.LIST_IGNORE;
      }
      const isUnder30MB = file.size / 1000 / 1000 < 30;
      if (!isUnder30MB) {
        message.error("File size must be 30 MB or smaller");
        return Upload.LIST_IGNORE;
      }
      return false;
    },
    onChange: (info) => {
      const latestFileList = info.fileList.slice(-1);
      setFileList(latestFileList);
      if (latestFileList.length > 0) {
        setIsFileSelected(true);
        form.setFieldsValue({
          fileName: latestFileList[0].name,
        });
      } else {
        setIsFileSelected(false);
        form.setFieldsValue({
          fileName: "",
        });
      }
    },
    fileList,
    accept: "*",
    multiple: false,
  };

  return (
    <Modal
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      centered
      destroyOnClose
      width={520}
      title={
        <span style={{ fontSize: "20px", fontWeight: 600 }}>
          <FontAwesomeIcon className="mr-2 " icon={faFile} />
          Add Document
        </span>
      }
    >
      {submitSuccess ? (
        <div
          style={{
            textAlign: "center",
            padding: "60px 20px",
          }}
        >
          <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: 72 }} />
          <h2 style={{ marginTop: 16 }}>Success!</h2>
          <p style={{ fontSize: "16px", color: "#555" }}>
            Your document has been uploaded successfully.
          </p>
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            type: "",
            originalFileName: "",
            notes: "",
            entityType: "Container",
          }}
        >
          <Form.Item
            name="documentTypeID"
            label="Document Type"
            rules={[
              {
                required: true,
                message: "Please select at least one document type",
              },
            ]}
          >
            <Select
              placeholder="Select document type(s)"
              allowClear
              loading={isLoading}
              disabled={initialValues?.originalFileName}
              mode="multiple"
            >
              {documentTypes?.map((doc) => (
                <Select.Option key={doc.id} value={doc.id}>
                  {doc.type}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Upload Document"
            required
            validateStatus={fileList.length === 0 ? "error" : "success"}
            rules={[{ required: true, message: "Please select file" }]}
            help={fileList.length === 0 ? "Please select a file to upload" : ""}
          >
            <Upload.Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon">
                <FontAwesomeIcon icon={faArrowUpFromBracket} className="text-4xl text-[#1890ff]" />
              </p>
              <p className="ant-upload-text">
                Click or drag file here to upload
              </p>
            </Upload.Dragger>
          </Form.Item>

          <Form.Item name="entityType" hidden>
            <Input />
          </Form.Item>

          {isFileSelected && (
            <Form.Item
              name="fileName"
              label="File Name"
              rules={[{ required: true, message: "Please enter file name" }]}
            >
              <Input placeholder="Enter File Name" />
            </Form.Item>
          )}

          <Form.Item name="notes" label="Notes">
            <TextArea
              className="!resize-none"
              rows={3}
              placeholder="Enter any additional notes"
            />
          </Form.Item>

          <Form.Item style={{ marginTop: 24 }}>
            <div className="flex justify-end">
              <AppButton variant="save"
                loading={isSubmitting}
                disabled={!form.getFieldValue("fileName")}
                onClick={async () => {
                  try {
                    const values = await form.validateFields();
                    const isMulti = initialValues?.duplicateCount > 1;
                    const isFileChanged = !!fileList[0]?.originFileObj; // true only if a new file was selected

                    if (isMulti && isFileChanged) {
                      showConfirmation(
                        "update",
                        initialValues?.originalFileName,
                        initialValues?.duplicateCount,
                        async () => {
                          await handleFinish(values);
                        }
                      );
                    } else {
                      await handleFinish(values);
                    }
                  } catch (err) { }
                }}>
                Save
              </AppButton>
              <AppButton variant="delete" // confirm delete modal not used here as no popover on button
                loading={isSubmitting}
                disabled={!form.getFieldValue("fileName") || !initialValues?.originalFileName}
                onClick={() => showConfirmation('delete', initialValues?.originalFileName, initialValues?.duplicateCount, handleDelete)}
              >
                Delete
              </AppButton>
              
              <AppButton variant="close"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Close
              </AppButton>
            </div>
          </Form.Item>
        </Form>
      )}
      <Modal
        open={modalConfig.open}
        title={modalConfig.title}
        onOk={modalConfig.onOk}
        onCancel={() => setModalConfig({ ...modalConfig, open: false })}
        okText={modalConfig.okText}
        cancelText={modalConfig.cancelText}
        okButtonProps={{ danger: modalConfig.type === 'warning' }}
        centered
      >
        {modalConfig.body}
      </Modal>
    </Modal>
  );
}